import { Controller } from '@hotwired/stimulus';

/**
 * Card selection - the board's selection mode. While on, clicking a card tile picks it instead of opening it,
 * the side panel shows how many are picked, and each panel form sends their ids (card_ids[]) when submitted.
 * A list's menu can pick all of its cards. A live refresh of the board (Turbo morph) keeps the selection: the
 * controller paints it again on the new tiles (turbo:morph@window->card-selection#render).
 * Targets: panel - the right sidebar (bd-select--empty while nothing is picked) | count - the "N selected" title | toggle - the toolbar button
 * @example
 * <turbo-frame data-controller="card-selection">
 *   <button type="button" data-card-selection-target="toggle" data-action="card-selection#toggle">Select</button>
 *   <div data-action="click->card-selection#pick"><a class="card-tile" data-card-id="1">…</a></div>
 *   <aside data-card-selection-target="panel" hidden><form data-action="submit->card-selection#fill">…</form></aside>
 * </turbo-frame>
 */
export default class extends Controller {
  static targets = ['panel', 'count', 'toggle'];

  // The state lives for the controller's lifetime: a page refresh (morph) can disconnect and reconnect the same
  // instance, and connect() then only paints the selection again.
  initialize () {
    this.selected = new Set();
    this.on = false;
  }

  connect () {
    this.render();
  }

  toggle () {
    this.on ? this.stop() : this.start();
  }

  start () {
    this.on = true;
    this.render();
  }

  stop () {
    this.on = false;
    this.selected.clear();
    this.render();
  }

  clear () {
    this.selected.clear();
    this.render();
  }

  // A tile click while selecting picks or unpicks the card (Turbo ignores the prevented click).
  pick (event) {
    if (!this.on) return;

    const tile = event.target.closest('.card-tile');
    if (!tile) return;

    event.preventDefault();
    const id = tile.dataset.cardId;
    this.selected.has(id) ? this.selected.delete(id) : this.selected.add(id);
    this.render();
  }

  // From a list's menu: selection mode with every card of that list picked.
  selectList (event) {
    const list = this.element.querySelector(`[data-list-id="${event.params.list}"]`);
    list?.querySelectorAll('.card-tile').forEach((tile) => this.selected.add(tile.dataset.cardId));
    event.target.closest('details')?.removeAttribute('open');
    this.start();
  }

  // Before a panel form goes: one card_ids[] field per picked card.
  fill (event) {
    const form = event.target;
    if (this.selected.size === 0) {
      event.preventDefault();
      return;
    }

    form.querySelectorAll('input[name="card_ids[]"]').forEach((input) => input.remove());
    this.selected.forEach((id) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'card_ids[]';
      input.value = id;
      form.appendChild(input);
    });
  }

  render () {
    this.element.classList.toggle('is-selecting', this.on);
    if (this.hasPanelTarget) {
      this.panelTarget.hidden = !this.on;
      this.panelTarget.classList.toggle('bd-select--empty', this.selected.size === 0);
    }
    this.toggleTargets.forEach((button) => button.setAttribute('aria-pressed', String(this.on)));
    this.element.querySelectorAll('.card-tile').forEach((tile) => {
      const picked = this.on && this.selected.has(tile.dataset.cardId);
      tile.classList.toggle('is-selected', picked);
      if (this.on) {
        tile.setAttribute('aria-pressed', String(picked));
      } else {
        tile.removeAttribute('aria-pressed');
      }
    });
    if (this.hasCountTarget) this.countTarget.textContent = this.countLabel();
  }

  countLabel () {
    const { zero, one, other } = this.countTarget.dataset;
    if (this.selected.size === 0) return zero;
    return (this.selected.size === 1 ? one : other).replace('%{count}', this.selected.size);
  }
}
